import { nanoid } from '@reduxjs/toolkit';
import {
  useState,
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useContext,
} from 'react';

type ContextProps = {
  key: string;
  reset: () => void;
  hasReset: boolean;
  complete: () => void;
};

export const WalkthroughContext = createContext<ContextProps>({
  reset: () => {},
  key: '',
  hasReset: false,
  complete: () => {},
});

export const WalkthroughProvider = ({ children }: PropsWithChildren) => {
  // Changing key forces the Walkthrough component to rerender
  const [key, setKey] = useState(nanoid());
  // Cleared on refreshing etc.
  const [hasReset, setHasReset] = useState(false);

  const complete = useCallback(() => {
    setHasReset(false);
  }, [setHasReset]);

  const reset = useCallback(() => {
    setKey(nanoid());
    setHasReset(true);
  }, [setKey, setHasReset]);

  const value = useMemo(
    () => ({
      key,
      reset,
      hasReset,
      complete,
    }),
    [key, hasReset, complete, reset]
  );

  return <WalkthroughContext.Provider value={value}>{children}</WalkthroughContext.Provider>;
};

export const useWalkthrough = () => {
  const vals = useContext(WalkthroughContext);
  if (!vals) throw new Error('WalkthroughContext is not defined');
  return vals;
};
